<template>
  <div class="auth-page">
    <div class="auth-page__content">
      <img src="@/assets/images/header-logo.png" alt="Lamar Health" class="auth-page__logo" />
      <h1 class="auth-page__title">{{ accountCreated ? "Account Created" : "Create Your Account" }}</h1>

      <div class="auth-page__card">
        <div v-if="linkInvalid" class="text-center">
          <v-alert type="error" variant="tonal" class="mb-4">
            This invite link is invalid or has expired. Ask your admin to resend.
          </v-alert>
          <v-btn color="primary" block size="large" @click="redirectToLogin"> Go To Login </v-btn>
        </div>

        <div v-else-if="accountCreated" class="text-center">
          <v-alert type="success" variant="tonal" class="mb-4"> Your account has been created. </v-alert>
          <v-btn color="primary" block size="large" @click="redirectToLogin"> Continue To Login </v-btn>
        </div>

        <v-form v-else @submit.prevent="submit">
          <v-text-field v-model="email" label="Email" disabled class="mb-4 lh--force-white-background" />

          <v-text-field
            v-model="firstName"
            label="First Name"
            :error-messages="firstNameError"
            class="mb-4 lh--force-white-background" />

          <v-text-field
            v-model="lastName"
            label="Last Name"
            :error-messages="lastNameError"
            class="mb-4 lh--force-white-background" />

          <v-text-field
            v-model="password"
            label="New Password"
            :type="showPassword ? 'text' : 'password'"
            :error-messages="passwordError"
            class="mb-4 lh--force-white-background"
            :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showPassword = !showPassword" />

          <v-text-field
            v-model="passwordConfirmation"
            label="Confirm Password"
            :type="showPasswordConfirmation ? 'text' : 'password'"
            :error-messages="passwordMatchError"
            class="mb-4 lh--force-white-background"
            :append-inner-icon="showPasswordConfirmation ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showPasswordConfirmation = !showPasswordConfirmation" />

          <v-btn color="primary" block size="large" type="submit" class="mb-4 mt-8" :loading="submitting">
            Create Account
          </v-btn>

          <v-alert v-if="errors.length > 0" type="error" variant="tonal" class="mb-4">
            <div v-for="error in errors" :key="error">{{ error }}</div>
          </v-alert>
        </v-form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import authService from "@/services/authService";
import { tryParseApiErrors } from "@/utils";
import { FORM_VALIDATION_PATTERNS } from "@/utils/constants";

const route = useRoute();
const router = useRouter();

const email = ref("");
const firstName = ref("");
const lastName = ref("");
const password = ref("");
const passwordConfirmation = ref("");
const showPassword = ref(false);
const showPasswordConfirmation = ref(false);

const submitting = ref(false);
const submitClicked = ref(false);
const accountCreated = ref(false);
const linkInvalid = ref(false);
const errors = ref([]);

const isPasswordValid = computed(() => FORM_VALIDATION_PATTERNS.PASSWORD_PATTERN.test(password.value));
const isPasswordMatchValid = computed(() => password.value === passwordConfirmation.value);

const firstNameError = computed(() =>
  submitClicked.value && !firstName.value.trim() ? "Please enter your first name" : ""
);
const lastNameError = computed(() =>
  submitClicked.value && !lastName.value.trim() ? "Please enter your last name" : ""
);

const passwordError = computed(() => {
  if (!submitClicked.value) return "";
  if (!password.value) return "Please enter a password";
  if (!isPasswordValid.value) {
    return "Please enter a valid password (at least 11 characters, at least 1 uppercase and 1 lowercase letter, 1 number, and 1 symbol)";
  }
  return "";
});

const passwordMatchError = computed(() => {
  if (!submitClicked.value) return "";
  if (!passwordConfirmation.value) return "Please confirm your password";
  if (!isPasswordMatchValid.value) return "Passwords must match";
  return "";
});

onMounted(async () => {
  const { uidb64, token } = route.params;
  if (!uidb64 || !token) {
    linkInvalid.value = true;
    return;
  }
  try {
    const { email: inviteEmail } = await authService.getInviteInfo(uidb64, token);
    email.value = inviteEmail;
  } catch {
    linkInvalid.value = true;
  }
});

const submit = async () => {
  submitClicked.value = true;
  if (firstNameError.value || lastNameError.value || passwordError.value || passwordMatchError.value) return;

  submitting.value = true;
  try {
    await authService.acceptInvite(route.params.uidb64, route.params.token, {
      first_name: firstName.value.trim(),
      last_name: lastName.value.trim(),
      password: password.value,
    });
    accountCreated.value = true;
    errors.value = [];
  } catch (error) {
    errors.value = tryParseApiErrors(error);
  } finally {
    submitting.value = false;
  }
};

const redirectToLogin = () => router.push({ name: "login" });
</script>

<style lang="scss" scoped>
@import "../styles/main.scss";

.auth-page {
  display: flex;
  flex-direction: column;
  flex: 1;
  background: #ffffff;
}

.auth-page__logo {
  height: 36px;
  width: auto;
  margin-bottom: 32px;
}

.auth-page__content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 16px 32px;
}

.auth-page__title {
  font-family: "DM Sans", sans-serif;
  font-size: 32px;
  font-weight: 700;
  color: #052e5e;
  margin-bottom: 32px;
}

.auth-page__card {
  width: 100%;
  max-width: 520px;
  background: #f4f6fa;
  border-radius: 16px;
  padding: 32px;
}

@media (max-width: 600px) {
  .auth-page__content {
    padding: 0 16px 24px;
  }

  .auth-page__logo {
    height: 32px;
    margin-bottom: 24px;
  }

  .auth-page__title {
    font-size: 26px;
    margin-bottom: 24px;
  }

  .auth-page__card {
    max-width: 420px;
    padding: 24px 20px;
  }
}
</style>
